import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Async thunks
export const performSearch = createAsyncThunk(
  'search/performSearch',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/search', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Search failed');
    }
  }
);

export const fetchFacets = createAsyncThunk(
  'search/fetchFacets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/search/facets');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch facets');
    }
  }
);

export const fetchSavedSearches = createAsyncThunk(
  'search/fetchSavedSearches',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.get('/api/search/saved', {
        params: { user_id: auth.user.id },
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch saved searches');
    }
  }
);

export const saveSearch = createAsyncThunk(
  'search/saveSearch',
  async ({ searchName, searchCriteria, notifyOnNew }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.post('/api/search/saved',
        {
          user_id: auth.user.id,
          search_name: searchName,
          search_criteria: searchCriteria,
          notify_on_new: notifyOnNew
        },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to save search');
    }
  }
);

export const deleteSavedSearch = createAsyncThunk(
  'search/deleteSavedSearch',
  async (searchId, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      await axios.delete(`/api/search/saved/${searchId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return searchId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete saved search');
    }
  }
);

export const fetchPriceAlerts = createAsyncThunk(
  'search/fetchPriceAlerts',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.get('/api/search/price-alerts', {
        params: { user_id: auth.user.id },
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch price alerts');
    }
  }
);

export const createPriceAlert = createAsyncThunk(
  'search/createPriceAlert',
  async ({ productId, targetPrice }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.post('/api/search/price-alerts',
        {
          user_id: auth.user.id,
          product_id: productId,
          target_price: targetPrice
        },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create price alert');
    }
  }
);

export const deletePriceAlert = createAsyncThunk(
  'search/deletePriceAlert',
  async (alertId, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      await axios.delete(`/api/search/price-alerts/${alertId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return alertId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete price alert');
    }
  }
);

const initialState = {
  results: [],
  facets: {
    categories: [],
    conditions: [],
    priceRanges: [],
    rarities: [],
  },
  savedSearches: [],
  priceAlerts: [],
  query: '',
  filters: {},
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
  },
  loading: false,
  error: null,
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery: (state, action) => {
      state.query = action.payload;
    },
    setSearchFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSearchFilters: (state) => {
      state.filters = {};
      state.query = '';
    },
    setSearchPage: (state, action) => {
      state.pagination.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Perform search
      .addCase(performSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(performSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload.products || action.payload;
        state.pagination.total = action.payload.total || state.results.length;
      })
      .addCase(performSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch facets
      .addCase(fetchFacets.fulfilled, (state, action) => {
        state.facets = action.payload;
      })
      // Fetch saved searches
      .addCase(fetchSavedSearches.fulfilled, (state, action) => {
        state.savedSearches = action.payload;
      })
      // Save search
      .addCase(saveSearch.fulfilled, (state, action) => {
        state.savedSearches.unshift(action.payload);
      })
      // Delete saved search
      .addCase(deleteSavedSearch.fulfilled, (state, action) => {
        state.savedSearches = state.savedSearches.filter(s => s.id !== action.payload);
      })
      // Fetch price alerts
      .addCase(fetchPriceAlerts.fulfilled, (state, action) => {
        state.priceAlerts = action.payload;
      })
      // Create price alert
      .addCase(createPriceAlert.fulfilled, (state, action) => {
        state.priceAlerts.unshift(action.payload);
      })
      // Delete price alert
      .addCase(deletePriceAlert.fulfilled, (state, action) => {
        state.priceAlerts = state.priceAlerts.filter(a => a.id !== action.payload);
      });
  },
});

export const { setQuery, setSearchFilters, clearSearchFilters, setSearchPage } = searchSlice.actions;
export default searchSlice.reducer;
